import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useCurrency } from "@/lib/currency";
import { useWholesaleAccount } from "@/hooks/useWholesaleAccount";
import { tierLabel } from "@/lib/wholesalePricing";
import { RecommendationCarousel } from "./RecommendationCarousel";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { items, isLoading, updateQuantity, removeItem, syncCart } = useCartStore();
  const { format, isUsd, currency } = useCurrency();
  const { account } = useWholesaleAccount();
  const discount = account?.active ? account.discount_percent / 100 : 0;
  const applyWholesale = (amount: number) => amount * (1 - discount);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const retailTotal = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
  const totalPrice = applyWholesale(retailTotal);

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  const handleCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={totalItems > 0 ? `Shopping cart, ${totalItems} item${totalItems !== 1 ? 's' : ''}` : "Shopping cart"}>
          <ShoppingCart className="h-5 w-5" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-primary text-primary-foreground">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display text-2xl">Shopping Cart</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''} in your cart`}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div key={item.variantId} className="flex gap-4 p-3 rounded-lg bg-card">
                      <div className="w-16 h-16 bg-secondary rounded-md overflow-hidden flex-shrink-0">
                        {item.product.node.images?.edges?.[0]?.node && (
                          <img src={item.product.node.images.edges[0].node.url} alt={item.product.node.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={64} height={64} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate">{item.product.node.title}</h4>
                        <p className="text-xs text-muted-foreground">{item.selectedOptions.map(o => o.value).join(' • ')}</p>
                        <p className="font-semibold text-sm text-primary">
                          {format(applyWholesale(parseFloat(item.price.amount)))}
                          {discount > 0 && (
                            <span className="ml-2 text-xs text-muted-foreground line-through">
                              {format(parseFloat(item.price.amount))}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <Button variant="ghost" size="icon" aria-label={`Remove ${item.product.node.title}`} className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeItem(item.variantId)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="icon" aria-label="Decrease quantity" className="h-6 w-6" onClick={() => updateQuantity(item.variantId, item.quantity - 1)}>
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm" aria-live="polite">{item.quantity}</span>
                          <Button variant="outline" size="icon" aria-label="Increase quantity" className="h-6 w-6" onClick={() => updateQuantity(item.variantId, item.quantity + 1)}>
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0 pt-4 border-t">
                <RecommendationCarousel
                  title="You may also like"
                  mode="cross-sell"
                  excludeVariantIds={items.map((i) => i.variantId)}
                  max={6}
                  className="pb-4"
                />
              </div>
              <div className="flex-shrink-0 space-y-4 pt-4 border-t">
                {discount > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">{tierLabel(account?.tier)} wholesale ({account!.discount_percent}% off)</span>
                    <span className="text-muted-foreground line-through">{format(retailTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-lg font-display font-semibold">Total</span>
                  <span className="text-xl font-bold text-primary">{format(totalPrice)}</span>
                </div>
                {!isUsd && (
                  <p className="text-xs text-muted-foreground -mt-2">
                    Prices shown in {currency} for reference. You'll be charged {`$${totalPrice.toFixed(2)} USD`} at checkout.
                  </p>
                )}
                <Button onClick={handleCheckout} className="w-full" size="lg" disabled={items.length === 0 || isLoading}>
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Checkout"}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
